import {useEffect, useState, useCallback} from 'react'
import {Button, Modal} from 'react-bootstrap'
import {DragDropContext, Droppable, Draggable, DropResult} from 'react-beautiful-dnd'
import {IKanbanStage} from '../../../types/kanban'

interface Props {
  show: boolean
  stages: IKanbanStage[]
  onClose: () => void
  onSave: (stageIds: string[]) => Promise<void>
}

export const ModalReorderKanbanStages: React.FC<Props> = ({show, stages, onClose, onSave}) => {
  const [localStages, setLocalStages] = useState<IKanbanStage[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (show) {
      setLocalStages(stages)
    }
  }, [show, stages])

  const moveStage = useCallback(
    (from: number, to: number) => {
      if (from === to || to < 0 || to >= localStages.length) return
      setLocalStages((prev) => {
        const next = Array.from(prev)
        const [removed] = next.splice(from, 1)
        next.splice(to, 0, removed)
        return next
      })
    },
    [localStages.length]
  )

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const {source, destination} = result
      if (!destination) return
      if (source.index === destination.index) return
      setLocalStages((prev) => {
        const next = Array.from(prev)
        const [removed] = next.splice(source.index, 1)
        next.splice(destination.index, 0, removed)
        return next
      })
    },
    [setLocalStages]
  )

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(localStages.map((stage) => stage.id))
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Reorder columns</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted'>Drag and drop to define the order of the columns.</p>
        <div className='kanban-reorder-list'>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='reorder-stages'>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {localStages.map((stage, index) => (
                    <Draggable draggableId={stage.id} index={index} key={stage.id}>
                      {(dragProvided, snapshot) => (
                        <div
                          className={`kanban-reorder-item ${
                            snapshot.isDragging ? 'kanban-reorder-item--dragging' : ''
                          }`}
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={{
                            ...dragProvided.draggableProps.style,
                            ...(stage.color
                              ? {
                                  borderLeft: `4px solid ${stage.color}`,
                                }
                              : null),
                          }}
                        >
                          <div className='kanban-reorder-item__handle' {...dragProvided.dragHandleProps}>
                            <i className='bi bi-grip-vertical' />
                          </div>
                          <div className='kanban-reorder-item__label'>{stage.name}</div>
                          <div className='kanban-reorder-item__actions'>
                            <Button
                              variant='light'
                              size='sm'
                              disabled={index === 0 || saving}
                              onClick={() => moveStage(index, index - 1)}
                            >
                              <i className='bi bi-arrow-up' />
                            </Button>
                            <Button
                              variant='light'
                              size='sm'
                              disabled={index === localStages.length - 1 || saving}
                              onClick={() => moveStage(index, index + 1)}
                            >
                              <i className='bi bi-arrow-down' />
                            </Button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save order'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
